import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Plus, X, AlertTriangle, Tag } from 'lucide-react'
import { useAuth } from '../../../../context/AuthContext'
import GroupEditor from './GroupEditor'
import BulkTagsModal from '../shared/BulkTagsModal'
import '../SponsorManager.css'

function groupField(g, ...keys) {
  for (const k of keys) {
    if (g?.[k] != null) return g[k]
  }
  return ''
}

const STATUS_TABS = [
  { value: 'active',   label: 'Active' },
  { value: 'pending',  label: 'Pending' },
  { value: 'archived', label: 'Archived' },
  { value: 'deleted',  label: 'Deleted' },
]

export default function GroupsList() {
  const { api } = useAuth()
  const navigate = useNavigate()
  const [groups, setGroups] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [stale, setStale] = useState(false)
  const [search, setSearch] = useState('')
  const [statusTab, setStatusTab] = useState('active')
  const [status, setStatus] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [selected, setSelected] = useState(new Set())
  const [bulkRunning, setBulkRunning] = useState(false)
  const [showBulkTags, setShowBulkTags] = useState(false)

  // Tag filter + sort
  const [tagFilter, setTagFilter] = useState('')
  const [allTbtvTags, setAllTbtvTags] = useState([])
  const [tagFilteredIds, setTagFilteredIds] = useState(null)
  const [sortBy, setSortBy] = useState('id')
  const [sortDir, setSortDir] = useState('asc')

  function handleSort(col) {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir('asc') }
  }

  const loadGroups = useCallback(async () => {
    try {
      const data = await api(`/sponsors/groups?status=${statusTab}`)
      setGroups(data.groups || [])
      setTotal(data.total || (data.groups?.length ?? 0))
      setStale(!!data.stale)
      if (data.ok === false) {
        const msg = data.error?.message || 'AdSpeed rejected the filter'
        const isDeleted = statusTab === 'deleted'
        setStatus(isDeleted
          ? `AdSpeed's getList API doesn't list deleted groups. The dashboard shows counts but not the items.`
          : `Error: ${msg}`)
      } else {
        setStatus('')
      }
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }, [api, statusTab])

  useEffect(() => {
    setLoading(true)
    loadGroups().finally(() => setLoading(false))
    setSelected(new Set())
  }, [loadGroups])

  useEffect(() => {
    api('/tags').then(d => setAllTbtvTags(d.tags || [])).catch(() => {})
  }, [api])

  useEffect(() => {
    if (!tagFilter) {
      setTagFilteredIds(null)
      return
    }
    api(`/sponsors/groups/by-tag?tag=${encodeURIComponent(tagFilter)}`)
      .then(d => {
        const ids = new Set((d.groups || []).map(g => String(g.id ?? g.GroupId)))
        setTagFilteredIds(ids)
      })
      .catch(e => setStatus(`Tag filter error: ${e.message}`))
  }, [api, tagFilter])

  async function handleRefresh() {
    setRefreshing(true)
    try {
      await api('/sponsors/cache/bust?prefix=groups:', { method: 'POST' })
      await loadGroups()
      setStatus('Refreshed from AdSpeed')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setRefreshing(false)
  }

  async function handleCreate(body) {
    const res = await api('/sponsors/groups', { method: 'POST', body: JSON.stringify(body) })
    if (res?.ok === false) {
      throw new Error(res.error?.message || 'AdSpeed rejected the request')
    }
    setShowCreate(false)
    setStatus('Group created')
    await loadGroups()
  }

  async function handleBulkTags(tags, mode) {
    const ids = Array.from(selected)
    setBulkRunning(true)
    let ok = 0, fail = 0
    for (const id of ids) {
      try {
        let finalTags = tags
        if (mode === 'add') {
          const existing = await api(`/sponsors/groups/${id}/tbtv-tags`)
          const existingNames = (existing?.tags || []).map(t => t.name)
          finalTags = Array.from(new Set([...existingNames, ...tags]))
        }
        await api(`/sponsors/groups/${id}/tbtv-tags`, {
          method: 'PUT',
          body: JSON.stringify({ tags: finalTags }),
        })
        ok++
      } catch {
        fail++
      }
    }
    setBulkRunning(false)
    setShowBulkTags(false)
    setSelected(new Set())
    setStatus(fail === 0 ? `Tagged ${ok} group(s).` : `Tagged ${ok}, ${fail} failed.`)
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return groups.filter(g => {
      const id = String(groupField(g, 'id', 'GroupId') || '')
      const name = String(groupField(g, 'name', 'Name') || '').toLowerCase()
      if (q && !name.includes(q) && !id.toLowerCase().includes(q)) return false
      if (tagFilteredIds && !tagFilteredIds.has(id)) return false
      return true
    })
  }, [groups, search, tagFilteredIds])

  const sorted = useMemo(() => {
    const arr = [...filtered]
    arr.sort((a, b) => {
      let av, bv
      if (sortBy === 'id') {
        av = Number(groupField(a, 'id', 'GroupId') || 0)
        bv = Number(groupField(b, 'id', 'GroupId') || 0)
      } else if (sortBy === 'description') {
        // Description comes from the locally cached info overlay
        // (cached_description) — not present on AdSpeed's getList rows.
        av = String(a.cached_description || '').toLowerCase()
        bv = String(b.cached_description || '').toLowerCase()
      } else {
        av = String(groupField(a, sortBy, sortBy.charAt(0).toUpperCase() + sortBy.slice(1)) || '').toLowerCase()
        bv = String(groupField(b, sortBy, sortBy.charAt(0).toUpperCase() + sortBy.slice(1)) || '').toLowerCase()
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [filtered, sortBy, sortDir])

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  function toggleSelectAll() {
    const allIds = sorted.map(g => String(groupField(g, 'id', 'GroupId')))
    if (allIds.every(id => selected.has(id))) setSelected(new Set())
    else setSelected(new Set(allIds))
  }

  const allVisibleSelected = sorted.length > 0
    && sorted.every(g => selected.has(String(groupField(g, 'id', 'GroupId'))))

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <h1>Groups</h1>
          <span className="sponsors-count">{filtered.length} of {total}</span>
          {stale && (
            <span className="sponsors-stale-badge">
              <AlertTriangle size={11} /> Stale cache
            </span>
          )}
        </div>
        <div className="sponsors-header-right">
          <button
            className="sponsors-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing || loading}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New group
          </button>
        </div>
      </div>

      {status && (
        <div className={`sponsors-status ${status.startsWith('Error') ? 'error' : 'success'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className="adtag-format-row" style={{ marginBottom: 10 }}>
        {STATUS_TABS.map(t => (
          <button
            key={t.value}
            className={`adtag-format-btn ${statusTab === t.value ? 'active' : ''}`}
            onClick={() => setStatusTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="zones-toolbar">
        <input
          className="zones-search"
          placeholder="Filter by name or id…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          value={tagFilter}
          onChange={e => setTagFilter(e.target.value)}
          className="zones-search"
          style={{ flex: '0 0 180px' }}
        >
          <option value="">All tags</option>
          {allTbtvTags.map(t => <option key={t} value={t}>tag: {t}</option>)}
        </select>
      </div>

      {selected.size > 0 && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'center',
          padding: '10px 14px', background: '#1e3a8a30', color: '#93c5fd',
          borderRadius: 6, marginBottom: 10, flexWrap: 'wrap',
        }}>
          <strong>{selected.size} selected</strong>
          <button
            className="sponsors-refresh-btn"
            onClick={() => setShowBulkTags(true)}
            disabled={bulkRunning}
          >
            <Tag size={13} /> Apply tags
          </button>
          <span className="muted" style={{ fontSize: '0.75rem' }}>
            (Group status changes must be done in AdSpeed's dashboard — same as zones.)
          </span>
          <button
            className="sponsors-refresh-btn"
            onClick={() => setSelected(new Set())}
            disabled={bulkRunning}
            style={{ marginLeft: 'auto' }}
          >
            <X size={13} /> Clear
          </button>
        </div>
      )}

      {loading ? (
        <div className="muted">Loading groups…</div>
      ) : sorted.length === 0 ? (
        <div className="muted">No groups found.</div>
      ) : (
        <table className="zones-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                />
              </th>
              {[
                { key: 'id',          label: 'ID' },
                { key: 'name',        label: 'Name' },
                { key: 'description', label: 'Description' },
                { key: 'status',      label: 'Status' },
              ].map(c => (
                <th
                  key={c.key}
                  onClick={() => handleSort(c.key)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  {c.label}
                  {sortBy === c.key && <span style={{ marginLeft: 4, color: '#f59e0b' }}>
                    {sortDir === 'asc' ? '↑' : '↓'}
                  </span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(g => {
              const id = String(groupField(g, 'id', 'GroupId'))
              const isSel = selected.has(id)
              return (
                <tr
                  key={id}
                  onClick={() => navigate(`/sponsors/groups/${id}`)}
                  style={isSel ? { background: '#1e3a8a30', cursor: 'pointer' } : { cursor: 'pointer' }}
                >
                  <td onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={isSel} onChange={() => toggleSelect(id)} />
                  </td>
                  <td>{id}</td>
                  <td>{groupField(g, 'name', 'Name')}</td>
                  <td className="muted" style={{ fontSize: '0.85rem' }}>
                    {g.cached_description || '—'}
                  </td>
                  <td>{groupField(g, 'status', 'Status')}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {showCreate && (
        <GroupEditor
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
        />
      )}

      {showBulkTags && (
        <BulkTagsModal
          count={selected.size}
          entityLabel="group"
          onClose={() => setShowBulkTags(false)}
          onSubmit={handleBulkTags}
        />
      )}
    </div>
  )
}
